import type { IRGSTransport } from './types';

export type GameAssetRoots = {
    gameAssets: string;
    genericAssets: string;
    staticRoot: string;
};

/** Resolve dist asset roots from a game client entry URL (index.html, with or without query). */
export function resolveAssetRootsFromClientEntry(clientEntry: string): GameAssetRoots {
    const baseUrl =
        typeof window !== 'undefined'
            ? new URL(clientEntry, window.location.href)
            : new URL(clientEntry, 'http://localhost/');
    const base = baseUrl.pathname.replace(/\/index\.html$/, '').replace(/\/$/, '');

    return {
        gameAssets: `${base}/assets`,
        genericAssets: `${base}/assets`,
        staticRoot: `${base}/`,
    };
}

function resolveClientEntryUrl(clientEntry: string): URL {
    return typeof window !== 'undefined'
        ? new URL(clientEntry, window.location.href)
        : new URL(clientEntry, 'http://localhost/');
}

export type GameLaunchOpenContext = 'modal' | 'new-tab' | 'same-tab';

export type BetsLimit = { min?: number | null; max?: number | null; default?: number | null };

/** Launcher-controlled UI options passed into the game at boot. */
export type GameLaunchUiOptions = {
    showLoadingScreen?: boolean;
    language?: string;
    currency?: string;
    denomination?: number;
    openContext?: GameLaunchOpenContext;
    betsLimit?: BetsLimit;
    cheatsEnabled?: boolean;
};

/** Provider-agnostic config the game needs to boot. */
export type GameRuntimeConfig = {
    gameId: string;
    assetRoots: GameAssetRoots;
    options?: GameLaunchUiOptions & Record<string, unknown>;
};

const LAUNCH_OPEN_CONTEXTS = new Set<GameLaunchOpenContext>(['modal', 'new-tab', 'same-tab']);

const CHEATS_ENABLED_PARAM = 'cheatsEnabled';
const CHEATS_ALLOWED_STORAGE_KEY = 'ATEMOYA_CHEATS_ALLOWED';

function parseCheatsEnabledParam(value: string | null): boolean | undefined {
    if (value === null) {
        return undefined;
    }
    if (value === '1' || value === 'true') {
        return true;
    }
    if (value === '0' || value === 'false') {
        return false;
    }
    return undefined;
}

function syncCheatsAllowedFlag(cheatsEnabled: boolean): void {
    window.ATEMOYA_CHEATS_ALLOWED = cheatsEnabled;
    try {
        window.sessionStorage.setItem(
            CHEATS_ALLOWED_STORAGE_KEY,
            cheatsEnabled ? '1' : '0'
        );
    } catch {
        // ignore storage errors (private mode/restrictions)
    }
}

/** Parse launcher UI options from a game client entry URL query string. */
export function parseLaunchUiOptionsFromClientEntry(clientEntry: string): GameLaunchUiOptions {
    try {
        const url = resolveClientEntryUrl(clientEntry);
        const options: GameLaunchUiOptions = {};
        const language = url.searchParams.get('language') ?? url.searchParams.get('locale');
        const currency = url.searchParams.get('currency');
        const denominationParam = url.searchParams.get('denomination');
        const openContext = url.searchParams.get('open_context');
        const cheatsEnabled = parseCheatsEnabledParam(url.searchParams.get(CHEATS_ENABLED_PARAM));
        if (language) {
            options.language = language;
        }
        if (currency) {
            options.currency = currency;
        }
        if (denominationParam !== null) {
            const denomination = Number(denominationParam);
            if (Number.isFinite(denomination)) {
                options.denomination = denomination;
            }
        }
        if (openContext && LAUNCH_OPEN_CONTEXTS.has(openContext as GameLaunchOpenContext)) {
            options.openContext = openContext as GameLaunchOpenContext;
        }
        if (cheatsEnabled !== undefined) {
            options.cheatsEnabled = cheatsEnabled;
        }
        return options;
    } catch {
        return {};
    }
}

/** Extract `game_*` id from a game client entry URL path. */
export function parseGameIdFromClientEntry(clientEntry: string): string | null {
    try {
        const url = resolveClientEntryUrl(clientEntry);
        const match = url.pathname.match(/\/games\/(game_[^/]+)\//);
        return match?.[1] ?? null;
    } catch {
        return null;
    }
}

/** Build full runtime config from a game client entry URL (path + query params). */
export function buildRuntimeConfigFromClientEntry(clientEntry: string): GameRuntimeConfig | null {
    const gameId = parseGameIdFromClientEntry(clientEntry);
    if (!gameId) {
        return null;
    }

    const launchOptions = parseLaunchUiOptionsFromClientEntry(clientEntry);

    return {
        gameId,
        assetRoots: resolveAssetRootsFromClientEntry(clientEntry),
        ...(Object.keys(launchOptions).length > 0 ? { options: launchOptions } : {}),
    };
}

/** Sync launcher context globals before the game UI mounts. */
export function applyLauncherContextFromConfig(config: GameRuntimeConfig): void {
    const openContext = config.options?.openContext;
    if (typeof openContext === 'string') {
        try {
            window.sessionStorage.setItem('open_context', openContext);
        } catch {
            // ignore storage errors (private mode/restrictions)
        }
    }

    if (typeof config.options?.cheatsEnabled === 'boolean') {
        syncCheatsAllowedFlag(config.options.cheatsEnabled);
    }
}

export type GameLaunchServices = {
    transport: IRGSTransport;

    onGameReady?: () => void;
    //TODO: Add other lifecycle hooks here
};

export type LaunchGameOptions = {
    config: GameRuntimeConfig;
    services: GameLaunchServices;
};

export type LaunchGameCleanup = () => void | Promise<void>;

export type AtemoyaGameFn = (
    rootElement: HTMLElement,
    options: LaunchGameOptions
) => Promise<LaunchGameCleanup>;

/** @deprecated Use AtemoyaGameFn */
export type LaunchGameFn = AtemoyaGameFn;

declare global {
    interface Window {
        atemoyaGame?: AtemoyaGameFn;
        ATEMOYA_CHEATS_ALLOWED?: boolean;
    }
}
